import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, MessageCircle, Send } from "lucide-react";
import "../lib/exportCapture";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Picker } from "./WaMessagesPanel";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const toB64 = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(blob); });
const recLabel = (r) => (String(r).endsWith("@g.us") ? `مجموعة واتساب (${String(r).slice(0, 6)}…)` : r);

const Recipients = ({ cfg, emps, ids, setIds, alerts, setAlerts, phones, setPhones }) => (
  <div className="space-y-3">
    <div className="space-y-1.5"><p className="text-xs font-bold text-slate-600">موظفون</p><Picker employees={emps} ids={ids} setIds={setIds} /></div>
    {cfg.recipients.length > 0 && (
      <div className="space-y-1" data-testid="wa-file-alerts"><p className="text-xs font-bold text-slate-600">أرقام ومجموعات تنبيهات الشركة</p>
        {cfg.recipients.map((r) => (
          <label key={r} className="flex items-center gap-2 text-sm text-slate-700" dir="auto">
            <input type="checkbox" checked={alerts.includes(r)} onChange={(e) => setAlerts(e.target.checked ? [...alerts, r] : alerts.filter((x) => x !== r))} data-testid={`wa-file-alert-${r}`} />
            <span dir="ltr">{recLabel(r)}</span></label>))}
      </div>)}
    <div className="space-y-1"><p className="text-xs font-bold text-slate-600">رقم جوال يدوي</p>
      <Input dir="ltr" value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="05xxxxxxxx, 9665xxxxxxxx" data-testid="wa-file-phones" /></div>
  </div>
);

export const WaExportPrompt = () => {
  const { user } = useAuth();
  const staff = user && user.kind !== "employee" && user.kind !== "marketer";
  const cfgRef = useRef(null);
  const [cfg, setCfg] = useState({ recipients: [] });
  const [file, setFile] = useState(null);
  const [emps, setEmps] = useState([]);
  const [ids, setIds] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [phones, setPhones] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { cfgRef.current = null; }, [user?.user_id]);
  useEffect(() => {
    if (!staff) return undefined;
    const onExport = async (e) => {
      if (/roster-monitor/i.test(e.detail?.filename || "")) return;
      if (!cfgRef.current) cfgRef.current = api.waExportConfig().catch(() => ({ active: false }));
      const c = await cfgRef.current;
      if (!c.active) return;
      setCfg(c);
      toast(`تم تصدير «${e.detail.filename}»`, {
        duration: 9000, id: "wa-export-toast",
        action: { label: "إرسال عبر واتساب", onClick: () => { setFile(e.detail); setIds([]); setAlerts([]); setPhones(""); setBody(""); } },
      });
    };
    window.addEventListener("wz-export", onExport);
    return () => window.removeEventListener("wz-export", onExport);
  }, [staff]);
  useEffect(() => { if (file && !emps.length) api.listEmployees().then((d) => setEmps((Array.isArray(d) ? d : []).filter((x) => x.status !== "terminated"))).catch(() => {}); }, [file, emps.length]);

  const phoneList = phones.split(/[,،\s]+/).filter(Boolean);
  const count = ids.length + alerts.length + phoneList.length;
  const send = async () => {
    setBusy(true);
    try {
      let blob = file.blob, filename = file.filename;
      if (!blob) {
        const res = await axios.get(file.url, { responseType: "blob" });
        const cd = res.headers["content-disposition"] || "";
        const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
        blob = res.data; if (m) filename = decodeURIComponent(m[1]);
      }
      const r = await api.waSendFile({ file_base64: await toB64(blob), filename, body, employee_ids: ids, alert_recipients: alerts, phones: phoneList });
      if (r.sent) { toast.success(`أُرسل الملف إلى ${r.sent} من ${r.total}`); setFile(null); }
      else toast.error(r.no_phone_count ? "لا يوجد رقم جوال صالح للمستلمين" : `لم يُرسل: ${r.failed?.[0]?.reason || "خطأ"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  if (!staff) return null;
  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && setFile(null)}>
      <DialogContent dir="rtl" className="w-[95vw] max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="wa-file-dialog">
        <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><MessageCircle className="h-5 w-5 text-emerald-600" /> إرسال الملف عبر واتساب</DialogTitle>
          <DialogDescription className="text-start">يُرسل الملف نفسه من رقم واتساب الشركة.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 text-sm" data-testid="wa-file-name"><FileText className="h-4 w-4 shrink-0 text-indigo-600" /><span className="truncate" dir="auto">{file?.filename}</span></div>
        <Recipients cfg={cfg} emps={emps} ids={ids} setIds={setIds} alerts={alerts} setAlerts={setAlerts} phones={phones} setPhones={setPhones} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="رسالة مرافقة (اختياري)" className="w-full rounded-lg border border-slate-200 p-3 text-sm" data-testid="wa-file-text" />
        <Button onClick={send} disabled={busy || !count} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="wa-file-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال إلى {count} مستلم
        </Button>
      </DialogContent>
    </Dialog>
  );
};
